import { useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Upload, CheckCircle2, AlertTriangle, GitCompareArrows, FileSpreadsheet } from "lucide-react";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (n ?? "—"));

export default function MutabakatIcra() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [onay, setOnay] = useState({});
  const [applying, setApplying] = useState(false);
  const fileRef = useRef(null);

  const onizle = async () => {
    if (!file) { toast.error("Önce Excel dosyası seçin"); return; }
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/icra/mutabakat/onizleme", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData(data);
      const init = {}; (data.degisiklikler || []).forEach((d) => { init[d.record_id] = true; });
      setOnay(init);
    } catch (e) { toast.error(e.response?.data?.detail || "Önizleme başarısız"); }
    finally { setLoading(false); }
  };

  const uygula = async () => {
    const items = (data.degisiklikler || []).filter((d) => onay[d.record_id]).map((d) => ({ record_id: d.record_id, set: d.set }));
    if (items.length === 0) { toast.error("Onaylı dosya yok"); return; }
    if (!window.confirm(`${items.length} dosya Excel'e göre güncellenecek (canlı veri). Devam edilsin mi?`)) return;
    setApplying(true);
    try {
      const { data: res } = await api.post("/icra/mutabakat/uygula", { items });
      toast.success(`${res.guncellenen} dosya güncellendi`);
      setData(null); setFile(null); setOnay({});
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { toast.error(e.response?.data?.detail || "Uygulama başarısız"); }
    finally { setApplying(false); }
  };

  const toggleAll = (v) => { const o = {}; (data.degisiklikler || []).forEach((d) => { o[d.record_id] = v; }); setOnay(o); };
  const o = data?.ozet || {};
  const secili = Object.values(onay).filter(Boolean).length;

  return (
    <Layout>
      <div data-testid="mutabakat-page">
        <div className="flex items-center gap-2 mb-1">
          <GitCompareArrows className="w-6 h-6 text-[#B8933F]" />
          <h1 className="font-heading text-2xl font-bold text-[#26231E]">İl Dışı Tahsilat Mutabakatı</h1>
        </div>
        <p className="text-sm text-[#8C8377] mb-5">Güncel İl Dışı tahsilat Excel'ini yükleyin; sistemle farklar dosya dosya gösterilir. Onayladıklarınız Excel'e göre güncellenir. Tahsilat/faiz farkı açılış değeri olarak işlenir (tarihli girişler korunur).</p>

        <div className="flex flex-wrap items-center gap-3 mb-5 p-4 bg-white border border-[#E7DECC] rounded-sm">
          <label htmlFor="mut-file" className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-[#E7DECC] rounded-sm cursor-pointer hover:border-[#B8933F]">
            <FileSpreadsheet className="w-5 h-5 text-[#B8933F]" />
            <span className="text-sm text-[#8C8377]">{file ? file.name : "Excel seçin (.xlsx)"}</span>
            <input id="mut-file" ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="mut-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <button onClick={onizle} disabled={loading} data-testid="mut-onizle-btn" className="flex items-center gap-1.5 px-5 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-50">
            <Upload className="w-4 h-4" /> {loading ? "Karşılaştırılıyor…" : "Önizle & Karşılaştır"}
          </button>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
              <Stat label="Excel Satır" v={o.excel_satir} />
              <Stat label="Farklı (mutabakat)" v={o.eslesen_farkli} accent="#B8933F" />
              <Stat label="Farksız" v={o.farksiz} accent="#2F6E4E" />
              <Stat label="Eşleşmeyen" v={o.eslesmeyen} accent="#A63A3A" />
              <Stat label="Çok Kayıtlı" v={o.cok_kayitli} accent="#7C5E9B" />
            </div>

            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-2">
                <button onClick={() => toggleAll(true)} className="text-xs px-2 py-1 border border-[#E7DECC] rounded-sm hover:bg-[#F3ECDD]">Tümünü Seç</button>
                <button onClick={() => toggleAll(false)} className="text-xs px-2 py-1 border border-[#E7DECC] rounded-sm hover:bg-[#F3ECDD]">Hiçbiri</button>
              </div>
              <button onClick={uygula} disabled={applying || secili === 0} data-testid="mut-uygula-btn" className="flex items-center gap-1.5 px-5 py-2 bg-[#2F6E4E] hover:bg-[#255c40] text-white rounded-sm text-sm font-medium disabled:opacity-50">
                <CheckCircle2 className="w-4 h-4" /> {applying ? "Uygulanıyor…" : `Onayla ve Uygula (${secili})`}
              </button>
            </div>

            <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden mb-6">
              <div className="max-h-[520px] overflow-auto">
                <table className="w-full text-sm" data-testid="mut-degisiklik-table">
                  <thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10"><tr>
                    <th className="px-2 py-2 kicker text-center w-10"></th>
                    <th className="px-3 py-2 kicker text-left">Borçlu</th>
                    <th className="px-3 py-2 kicker text-left">Dosya/İl</th>
                    <th className="px-3 py-2 kicker text-left">Değişecek Alanlar (Excel ← Sistem)</th>
                  </tr></thead>
                  <tbody>
                    {(data.degisiklikler || []).length === 0 ? (
                      <tr><td colSpan={4} className="px-3 py-10 text-center text-[#8C8377]">Fark bulunamadı — her şey güncel.</td></tr>
                    ) : data.degisiklikler.map((d) => (
                      <tr key={d.record_id} className="border-b border-[#EFE7D6] hover:bg-[#F3ECDD]" data-testid={`mut-row-${d.record_id}`}>
                        <td className="px-2 py-2 text-center">
                          <input type="checkbox" checked={!!onay[d.record_id]} onChange={(e) => setOnay((p) => ({ ...p, [d.record_id]: e.target.checked }))} data-testid={`mut-check-${d.record_id}`} />
                        </td>
                        <td className="px-3 py-2 font-medium">{d.borclu}</td>
                        <td className="px-3 py-2 text-[#8C8377]">{d.dosya_no || "—"}<br /><span className="text-xs">{d.il}</span></td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1.5">
                            {d.farklar.map((f, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs bg-[#B8933F]/10">
                                <b className="text-[#8C8377]">{f.alan}:</b>
                                <span className="text-[#2F6E4E] font-semibold">{fmt(f.excel)}</span>
                                <span className="text-[#A63A3A] line-through">{fmt(f.sistem)}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {(data.eslesmeyen?.length > 0 || data.cok_kayitli?.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-[#A63A3A]/20 rounded-sm p-4" data-testid="mut-eslesmeyen">
                  <div className="flex items-center gap-2 text-[#A63A3A] font-semibold text-sm mb-2"><AlertTriangle className="w-4 h-4" /> Eşleşmeyen (Excel'de var, sistemde yok) — dokunulmadı</div>
                  <div className="max-h-52 overflow-auto text-sm text-[#6B6459] space-y-0.5">
                    {(data.eslesmeyen || []).map((b, i) => <div key={i}>• {b}</div>)}
                    {(data.eslesmeyen || []).length === 0 && <div className="text-[#8C8377]">Yok</div>}
                  </div>
                </div>
                <div className="bg-white border border-[#7C5E9B]/20 rounded-sm p-4" data-testid="mut-cok-kayitli">
                  <div className="flex items-center gap-2 text-[#7C5E9B] font-semibold text-sm mb-2"><AlertTriangle className="w-4 h-4" /> Çok Kayıtlı İsim (birden fazla dosya) — dokunulmadı</div>
                  <div className="max-h-52 overflow-auto text-sm text-[#6B6459] space-y-0.5">
                    {(data.cok_kayitli || []).map((c, i) => <div key={i}>• {c.borclu} <span className="text-[#8C8377]">({c.adet} dosya)</span></div>)}
                    {(data.cok_kayitli || []).length === 0 && <div className="text-[#8C8377]">Yok</div>}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function Stat({ label, v, accent }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm p-3">
      <div className="kicker">{label}</div>
      <div className="font-heading font-bold text-xl mt-0.5 tabular-nums" style={accent ? { color: accent } : undefined}>{v ?? 0}</div>
    </div>
  );
}
